import camelcaseKeys from 'camelcase-keys';
import apiUrlProvider, {
  REQUEST_PLACED_PAYMENT,
  PAYMENT_CHECKOUT,
  PAYMENT_STRIPE_CHECKOUT,
  REQUEST_PAYMENT_METHOD,
  REQUEST_PAYMENT_CONFIRM,
  REQUEST_CHECK_QR,
  REQUEST_QR_PAYMENT,
  REQUEST_RECEIPT,
  REQUEST_SEND_RECEIPT,
  REQUEST_SEND_RECEIPT_CASHBACK,
  REQUEST_COLLECT_LOYALTY,
  REQUEST_LOYALTY_ACTIVITY,
  REQUEST_LOYALTY,
  REQUEST_UPDATE_PAY_LATER,
  REQUEST_CURRENT_RESERVE_LOYALTY,
  REQUEST_AMOUNT_LESS,
  GET_AMOUNT_LESS_MERCHANT_DETAIL,
  SPLIT_CHECKOUT_ITEM,
  REQUEST_SETUP_CONTINENT_FEE,
  REQUEST_SUBSCRIBE_PAYMENT_EMAIL,
  REQUEST_CHARGE_TO_ROOM,
  REQUEST_ADYEN_PAYMENT,
  REQUEST_ADYEN_PAYMENT_CONFIRM,
  REQUEST_ADYEN_PAYMENT_METHODS,
} from 'constants/api-endpoints';
import { fetchPost, fetchGet, fetchPut } from 'utils/services/fetch';

export const requestPlacingPayment = (token) => {
  const url = apiUrlProvider.get(REQUEST_PLACED_PAYMENT);
  return fetchGet(url, {}, { isAuth: true, token }).then((response) => {
    const { tips } = response;
    const newObj = camelcaseKeys(response, { deep: true });
    return {
      ...newObj,
      tips: tips?.map((tip) => {
        return parseInt(tip * 100, 10);
      }),
    };
  });
};

export const paymentCheckout = (token, nonce) => {
  const url = apiUrlProvider.get(PAYMENT_CHECKOUT);
  return fetchPost(url, { nonce }, { isAuth: true, token }).then((response) => response);
};

export const paymentStripeCheckout = (token, payload = {}) => {
  const url = apiUrlProvider.get(PAYMENT_STRIPE_CHECKOUT);
  return fetchPost(url, payload, { isAuth: true, token }).then((response) => response);
};

export const requestStripeSlientSec = (token, tipPercentage = 0, type, paymentMethodId) => {
  const url = apiUrlProvider.get(REQUEST_PAYMENT_METHOD);
  return fetchPost(
    url,
    {
      tip_percentage: tipPercentage,
      payment_method: type,
      paymentMethodId,
    },
    { isAuth: true, token },
  ).then((response) => response.client_secret);
};

export const requestStripeConfirmPayment = (token, payload = {}) => {
  const url = apiUrlProvider.get(REQUEST_PAYMENT_CONFIRM);
  return fetchPost(url, payload, { isAuth: true, token }).then((response) => response);
};

export const requestCheckQrCode = (qrid) => {
  const url = apiUrlProvider.get(REQUEST_CHECK_QR, { id: qrid });
  return fetchGet(url, {}).then((response) => {
    const newObj = camelcaseKeys(response.data, { deep: true });
    return newObj;
  });
};

export const requestQrPayment = (qrid, payload) => {
  const url = apiUrlProvider.get(REQUEST_QR_PAYMENT, { id: qrid });
  return fetchPost(url, payload, { isAuth: true }).then((response) => response.token);
};

export const requestReceipt = (token) => {
  const url = apiUrlProvider.get(REQUEST_RECEIPT);
  return fetchGet(url, {}, { isAuth: true, token }).then((response) => {
    const authorizationDate =
      response.authorization_date ||
      response.authorisation_date ||
      response.authorized_at ||
      response.authorised_at ||
      response.authorizationDate ||
      response.authorisationDate ||
      null;

    const captureDate =
      response.capture_date ||
      response.captured_at ||
      response.captureDate ||
      response.capturedAt ||
      null;

    const isPreAuth =
      response.preAuth === true ||
      response.pre_auth === true ||
      String(response.status || '').toLowerCase() === 'authorized' ||
      String(response.status || '').toLowerCase() === 'authorised';

    const {
      id,
      amount,
      cashback_to,
      cashback_amount,
      qr_stand,
      date,
      loyalty_reward,
      review_engine,
      merchant_contact_data: { logo_image, company_name, address, email },
      tip_amount,
      charge_amount,
      payload,
      tip_percentage,
      cashback_transaction_id,
      type,
      taxAmount,
      discount,
      receiptUrl,
      extraFee,
      orderDetails,
    } = response;

    const baseAddress = {
      address_first_line: '',
      address_second_line: '',
      street_name: '',
      house_number: '',
      city: '',
      regios: '',
      country: '',
      zip_code: '',
    };

    const newAddress = [];
    for (const property in baseAddress) {
      if (!!address && !!address[property]) {
        newAddress.push(address[property]);
      }
    }

    const newObj = {
      splitBill: response.splitBill,
      id,
      amount,
      cashbackTo: cashback_to,
      cashbackAmount: cashback_amount,
      qrStand: qr_stand,
      date,
      loyaltyReward: loyalty_reward,
      reviewEngine: review_engine,
      logoImage: logo_image,
      companyName: company_name,
      tip_amount,
      charge_amount,
      address: newAddress.join(' '),
      payload,
      tip_percentage,
      cashback_transaction_id,
      type,
      taxAmount,
      discount,
      merchantEmail: email,
      receiptUrl,
      // Proof-of-payment QR on the receipt; an older API that does not send it means "show".
      showReceiptQr: response.show_receipt_qr !== false,
      extraFee,
      orderDetails,
      authorizationDate,
      captureDate,
      isPreAuth,
    };

    return newObj;
  });
};

export const requestSendReceipt = (token, payload) => {
  const url = apiUrlProvider.get(REQUEST_SEND_RECEIPT);
  return fetchPost(url, payload, { isAuth: true, token }).then((response) => {
    const newObj = camelcaseKeys(response, { deep: true });
    return newObj;
  });
};

export const requestSendReceiptCashback = (token, tx, payload) => {
  const url = apiUrlProvider.get(REQUEST_SEND_RECEIPT_CASHBACK, { tx });
  return fetchPost(url, payload, { isAuth: true, token }).then((response) => {
    const newObj = camelcaseKeys(response, { deep: true });
    return newObj;
  });
};

export const requestCollectLoyalty = (token, payload = {}, countryCode) => {
  const url = apiUrlProvider.get(REQUEST_COLLECT_LOYALTY);

  return fetchPost(url, payload, {
    isAuth: true,
    token,
    localization: countryCode,
  }).then((response) => response);
};

export const requestLoyaltyActivity = (token, phoneNumber) => {
  const url = apiUrlProvider.get(REQUEST_LOYALTY_ACTIVITY, {
    phone: phoneNumber,
  });
  return fetchGet(url, {}, { isAuth: true, token }).then((response) => {
    const newObj = camelcaseKeys(response, { deep: true });
    return newObj;
  });
};

export const requestLoyalty = (token) => {
  const url = apiUrlProvider.get(REQUEST_LOYALTY);
  return fetchGet(url, {}, { isAuth: true, token }).then((response) => {
    const newObj = camelcaseKeys(response.data, { deep: true });
    return newObj;
  });
};

export const reqGetCurrentReserveLoyalty = (token) => {
  const url = apiUrlProvider.get(REQUEST_CURRENT_RESERVE_LOYALTY);
  return fetchGet(url, {}, { isAuth: true, token }).then((response) => {
    const newObj = camelcaseKeys(response, { deep: true });
    return newObj;
  });
};

export const reqUpdatePayLater = (token, payload = {}) => {
  const url = apiUrlProvider.get(REQUEST_UPDATE_PAY_LATER);
  return fetchPut(url, payload, { isAuth: true, token });
};

export const reqUpdateAmount = (token, payload = {}) => {
  const url = apiUrlProvider.get(REQUEST_AMOUNT_LESS);
  return fetchPut(url, payload, { isAuth: true, token });
};

export const requestGetMerchantDetail = (token) => {
  const url = apiUrlProvider.get(GET_AMOUNT_LESS_MERCHANT_DETAIL);
  return fetchGet(url, {}, { isAuth: true, token }).then((response) => {
    const newObj = camelcaseKeys(response, { deep: true });
    return newObj;
  });
};

export const reqCheckoutSplitPayment = (token, payload = {}) => {
  const url = apiUrlProvider.get(SPLIT_CHECKOUT_ITEM);
  return fetchPost(url, payload, { isAuth: true, token });
};

export const reqSetupContinentFee = (token, paymentMethodId) => {
  const url = apiUrlProvider.get(REQUEST_SETUP_CONTINENT_FEE);
  return fetchPost(url, { paymentMethodId }, { isAuth: true, token });
};

export const reqSubScribePaymentEmail = (token, email) => {
  const url = apiUrlProvider.get(REQUEST_SUBSCRIBE_PAYMENT_EMAIL);
  return fetchPost(url, { email }, { isAuth: true, token });
};

export const reqVerifyRoom = (token, payload) => {
  const url = apiUrlProvider.get(REQUEST_CHARGE_TO_ROOM);
  return fetchPost(url, payload, { isAuth: true, token });
};

export const reqRequestAdyenPayment = (token, payload) => {
  const url = apiUrlProvider.get(REQUEST_ADYEN_PAYMENT);
  return fetchPost(url, payload, { isAuth: true, token });
};

export const reqConfirmAdyenPayment = (token, payload) => {
  const url = apiUrlProvider.get(REQUEST_ADYEN_PAYMENT_CONFIRM);
  return fetchPost(url, payload, { isAuth: true, token });
};

export const reqPaymentMethosAdyen = (token) => {
  const url = apiUrlProvider.get(REQUEST_ADYEN_PAYMENT_METHODS);
  return fetchGet(url, {}, { isAuth: true, token });
};
